import type { CanvasEdge, CanvasNode, ScenarioStructure } from "@opus-dt/shared-types";
import type { MachineDetail } from "@/types/lines.types";
import type {
  ComparisonResult,
  RunKpis,
  SandboxDraft,
  SandboxMachine,
} from "@/types/sim-sandbox.types";
import { DEFAULT_PERIOD } from "@/types/sim-sandbox.types";
import {
  deriveEdgesFromStages,
  layeringOf,
  stagesFromLayering,
} from "./SandboxCanvas/flow-helpers";

interface HydrateInput {
  scenarioId: string | null;
  baseLineId: string;
  baseUpdatedAt: string | null;
  productId: string | null;
  machines: MachineDetail[];
  nodes: CanvasNode[];
  edges: CanvasEdge[];
}

// Constrói o rascunho do sandbox a partir da linha real (máquinas + layout). START/END viram
// âncoras; nós MACHINE usam o machineId como key (casa com machines[].key). Arestas de layout
// (ligam NÓS) são traduzidas para keys de máquina; arestas de/para START/END são preservadas
// como conexões âncora, mas só arestas máquina→máquina entram em `edges` do snapshot.
export function buildDraftFromLine(input: HydrateInput): SandboxDraft {
  const machineByLayoutNode = new Map<string, string>();
  let start = { key: "__start__", x: 0, y: 0 };
  let end = { key: "__end__", x: 600, y: 0 };

  for (const n of input.nodes) {
    if (n.type === "MACHINE" && typeof n.data?.machineId === "string") {
      machineByLayoutNode.set(n.id, n.data.machineId);
    } else if (n.type === "START") {
      start = { key: n.id, x: n.x, y: n.y };
    } else if (n.type === "END") {
      end = { key: n.id, x: n.x, y: n.y };
    }
  }

  const posByMachine = new Map<string, { x: number; y: number }>();
  for (const n of input.nodes) {
    const mid = machineByLayoutNode.get(n.id);
    if (mid) posByMachine.set(mid, { x: n.x, y: n.y });
  }

  // Arestas máquina→máquina da linha (START/END são só layout) — insumo do layering.
  const nodeKey = (nodeId: string): string | null => {
    const mid = machineByLayoutNode.get(nodeId);
    if (mid) return mid;
    if (nodeId === start.key) return start.key;
    if (nodeId === end.key) return end.key;
    return null;
  };
  const anchorKeys = new Set([start.key, end.key]);
  const lineEdges = input.edges
    .filter((e) => e.type === "PRODUCTION_FLOW")
    .map((e) => ({ sourceKey: nodeKey(e.sourceNodeId), targetKey: nodeKey(e.targetNodeId) }))
    .filter(
      (e): e is { sourceKey: string; targetKey: string } =>
        e.sourceKey != null &&
        e.targetKey != null &&
        !anchorKeys.has(e.sourceKey) &&
        !anchorKeys.has(e.targetKey)
    );

  // Inferir estágios (camadas) do layering topológico das edges da linha; cada máquina
  // ganha seu stageKey e as edges do snapshot são RE-derivadas do modelo em camadas (canônico).
  const machineIds = input.machines.map((m) => m.id);
  const { stages, stageKeyOf } = stagesFromLayering(layeringOf(machineIds, lineEdges));

  const machines: Record<string, SandboxMachine> = {};
  input.machines.forEach((m, i) => {
    const pos = posByMachine.get(m.id) ?? { x: 120 + i * 160, y: 0 };
    machines[m.id] = {
      key: m.id,
      sourceMachineId: m.id,
      origin: "line",
      present: true,
      code: m.code,
      name: m.name,
      x: pos.x,
      y: pos.y,
      stageKey: stageKeyOf.get(m.id) ?? stages[0]?.key ?? "stage-1",
      provenance: "assumed",
    };
  });

  return {
    scenarioId: input.scenarioId,
    baseLineId: input.baseLineId,
    baseUpdatedAt: input.baseUpdatedAt,
    productId: input.productId,
    machines,
    stages,
    edges: deriveEdgesFromStages(Object.values(machines), stages),
    start,
    end,
    period: { ...DEFAULT_PERIOD },
    selectedKey: null,
    dirty: false,
  };
}

// Reabre um cenário salvo do `structureSnap` (preserva liga/desliga, overrides,
// máquinas adicionadas e conexões). code/name (fora do contrato) vêm de `names` (linha + acervo);
// máquina sem nome resolvido cai num rótulo neutro (nunca inventa).
export function buildDraftFromSnapshot(
  snap: ScenarioStructure,
  ctx: {
    scenarioId: string;
    baseUpdatedAt: string | null;
    names: Map<string, { code: string; name: string }>;
  }
): SandboxDraft {
  let start = { key: "__start__", x: 0, y: 0 };
  let end = { key: "__end__", x: 600, y: 0 };
  const posByKey = new Map<string, { x: number; y: number }>();
  for (const n of snap.canvas.nodes) {
    if (n.nodeType === "START") start = { key: n.key, x: n.x, y: n.y };
    else if (n.nodeType === "END") end = { key: n.key, x: n.x, y: n.y };
    else posByKey.set(n.key, { x: n.x, y: n.y });
  }

  const machines: Record<string, SandboxMachine> = {};
  snap.machines.forEach((m, i) => {
    const pos = posByKey.get(m.key) ?? { x: 120 + i * 160, y: 0 };
    const info = ctx.names.get(m.sourceMachineId);
    machines[m.key] = {
      key: m.key,
      sourceMachineId: m.sourceMachineId,
      origin: m.origin,
      present: m.present,
      code: info?.code ?? "—",
      name: info?.name ?? "Máquina",
      x: pos.x,
      y: pos.y,
      stageKey: m.stageKey,
      simProfile: m.simProfile,
      simOverrides: m.simOverrides,
      provenance: m.simProfile?.provenance === "measured" ? "measured" : "assumed",
    };
  });

  return {
    scenarioId: ctx.scenarioId,
    baseLineId: snap.baseLineId,
    baseUpdatedAt: ctx.baseUpdatedAt,
    productId: snap.productId || null,
    machines,
    stages: snap.stages,
    edges: snap.edges,
    start,
    end,
    period: { ...DEFAULT_PERIOD },
    selectedKey: null,
    dirty: false,
  };
}

const pp = (a: number | null, b: number | null): number | null =>
  a == null || b == null ? null : Number((a - b).toFixed(1));

// Compara a rodada (candidate) contra o baseline: Δ OEE, Δ produção, deslocamento de gargalo.
export function composeComparison(candidate: RunKpis, baseline: RunKpis): ComparisonResult {
  const a = [...candidate.bottleneckNames].sort().join("|");
  const b = [...baseline.bottleneckNames].sort().join("|");
  return {
    deltaOee: pp(candidate.oee, baseline.oee),
    deltaProduction:
      candidate.estimatedProduction == null || baseline.estimatedProduction == null
        ? null
        : candidate.estimatedProduction - baseline.estimatedProduction,
    bottleneckShifted: a !== b,
  };
}
